/**
 * MemeLaunch / Dextoro Treasury Configuration
 * 
 * Central location for treasury wallet address and related utilities.
 * This wallet receives the "partner" share of DBC fees:
 * - 70% of trading fees from bonding curve swaps
 * - 100% of migration fees when pools complete
 * - Referral fees from swaps (if enabled)
 * 
 * FEE ROUTING ARCHITECTURE:
 * 
 * The Meteora DBC config has a `feeClaimer` field that must be set to this
 * treasury wallet when the config is created. All partner fees are then
 * automatically routed to token accounts owned by this wallet.
 * 
 * For detailed fee split configuration, see src/lib/meteora/feeConfig.ts
 */

import { PublicKey } from '@solana/web3.js';
import { getAssociatedTokenAddress } from '@solana/spl-token';
import { debug } from "@/lib/debug";

// ============================================================================
// TREASURY WALLET CONFIGURATION
// ============================================================================

/**
 * MemeLaunch Treasury Wallet (receives partner share of all fees)
 * 
 * This address must match the `feeClaimer` field in the on-chain DBC config.
 * All partner fees (70% of trading fees, 100% of migration fees) are routed here.
 * 
 * @see MEMELAUNCH_TREASURY_PUBKEY in src/lib/meteora/feeConfig.ts (canonical source)
 */
export const DEXTORO_TREASURY_WALLET = new PublicKey(
  '9ynRciWU7m3vGtGmm6s9YRW2avXA5D64AMbcedhakxQL'
);

/**
 * Alias for consistency with feeConfig.ts naming
 */
export const MEMELAUNCH_TREASURY_WALLET = DEXTORO_TREASURY_WALLET;

// ============================================================================
// TOKEN MINTS
// ============================================================================

/**
 * Native SOL mint (wrapped SOL) - used for fee collection and trading
 */
export const WSOL_MINT = new PublicKey(
  'So11111111111111111111111111111111111111112'
);

// ============================================================================
// ASSOCIATED TOKEN ACCOUNTS
// ============================================================================

/**
 * Get the Treasury's Associated Token Account for WSOL
 * This is where partner trading fees (in WSOL) are collected.
 */
export async function getTreasuryWsolAta(): Promise<PublicKey> {
  return getAssociatedTokenAddress(WSOL_MINT, DEXTORO_TREASURY_WALLET);
}

/**
 * Get an Associated Token Account for any wallet and mint
 */
export async function getAssociatedTokenAccountAddress(
  walletAddress: PublicKey,
  mintAddress: PublicKey
): Promise<PublicKey> {
  return getAssociatedTokenAddress(mintAddress, walletAddress);
}

/**
 * Get referral token account for trades
 * 
 * Currently returns the treasury WSOL ATA for all trades.
 * 
 * FUTURE ENHANCEMENT:
 * Could implement per-creator referral routing by:
 * 1. Looking up creator_address from launched_tokens table by mint
 * 2. Deriving their WSOL ATA
 * 3. Returning that ATA for referral fees on their token's trades
 * 
 * For now, referral fees go to the treasury.
 */
export async function getReferralAccountForMint(
  _mintAddress: string,
  _creatorAddress?: string
): Promise<PublicKey> {
  // All referral fees currently go to the treasury
  debug.trace('[Treasury] Getting referral account - using treasury (creator rev-share not yet implemented)');
  return getTreasuryWsolAta();
}

// ============================================================================
// LOGGING
// ============================================================================

debug.trace('[Treasury] MemeLaunch Treasury Wallet:', DEXTORO_TREASURY_WALLET.toBase58());
debug.trace('[Treasury] WSOL Mint:', WSOL_MINT.toBase58());
